import type { DeliverySlot as DeliverySlotType } from '@@partner:types:app'
import type { GQL_ID } from '@@partner:types:graphql'

import { SlotInfoInput } from '@@partner:graphql/mutation/shop/updateShopSlotInfo.gql'
import { mapToGQL_TIMESTAMP } from '@@partner:helpers/data-mappers/timestamp'
import { moveDateToTimezone } from '@@partner:helpers/util'

export type SlotStateType = Partial<DeliverySlotType> & {
  id?: GQL_ID
  shop_id?: GQL_ID
  zone_id?: GQL_ID
  ts_start: Date
  ts_end: Date
  timeout: number
  orders_capacity?: number
  position_capacity?: number
  timezone: number
}

export const SLOT_DEFAULT_STATE: SlotStateType = {
  shop_id: undefined,
  zone_id: undefined,
  ts_start: new Date(),
  ts_end: new Date(),
  timeout: 0,
  orders_capacity: 0,
  position_capacity: 0,
  timezone: 4
}

/*
  Used to convert backend (ms) timeout value to minutes
*/

const SLOT_TIMEOUT_DENSITY_FACTOR = 60000

/*
  Default values
*/

const DEFAULT_ORDERS_CAPACITY = 40
const DEFAULT_POSITIONS_CAPACITY = 1000

export const makeDefaultState = (id: GQL_ID): SlotStateType =>
  ({
    ...SLOT_DEFAULT_STATE,
    id
  } as SlotStateType)

const getSlotTzOffset = (slot: DeliverySlotType): number =>
  slot?.shop?.tzOffset ?? slot?.deliveryZone?.tzOffset ?? 0

export const mapApiDataToState = (slot: DeliverySlotType): SlotStateType => {
  const tzOffset = getSlotTzOffset(slot)
  return {
    id: slot.id,
    shop_id: slot.shop_id,
    zone_id: slot.zone_id,
    ts_start: new Date(moveDateToTimezone(parseInt(slot.period.from), tzOffset)),
    ts_end: new Date(moveDateToTimezone(parseInt(slot.period.to), tzOffset)),
    timeout: slot.timeout / SLOT_TIMEOUT_DENSITY_FACTOR,
    orders_capacity: slot.orders_capacity,
    position_capacity: slot.position_capacity,
    timezone: tzOffset / 3600
  }
}

export const mapStateToApiVariable = (state: SlotStateType): SlotInfoInput => ({
  id: state.id,
  shop_id: state.shop_id,
  zone_id: state.zone_id,
  ts_start: mapToGQL_TIMESTAMP(state.ts_start),
  ts_end: mapToGQL_TIMESTAMP(state.ts_end),
  timeout: Number(state.timeout) * SLOT_TIMEOUT_DENSITY_FACTOR,
  orders_capacity: Number(state.orders_capacity) ?? DEFAULT_ORDERS_CAPACITY,
  position_capacity:
    Number(state.position_capacity) ?? DEFAULT_POSITIONS_CAPACITY
})
